import type { AnyRecord } from './api'

export interface PcVipPlanPresentation {
  available: boolean
  planId: number | null
  primary: string
  secondary: string
  renewal: string
  cta: string
}

const PERIOD_BY_DURATION_TYPE: Record<number, string> = {
  1: 'month',
  2: 'quarter',
  3: 'year',
  4: 'week',
  5: 'day',
}

function normalizePrice(value: unknown): string {
  const raw = String(value ?? '').trim()
  if (!/^\d+(?:\.\d{1,2})?$/.test(raw)) return ''
  const amount = Number(raw)
  if (!Number.isFinite(amount) || amount <= 0) return ''
  return `$${amount.toFixed(2)}`
}

function normalizePeriod(plan: AnyRecord): string {
  const serverPeriod = String(plan?.subscription_period_text ?? '').trim().toLowerCase()
  if (['day', 'week', 'month', 'quarter', 'year'].includes(serverPeriod)) return serverPeriod
  return PERIOD_BY_DURATION_TYPE[Number(plan?.duration_type)] || ''
}

function hasIntroDiscount(plan: AnyRecord): boolean {
  const flag = plan?.has_first_month_discount
  return flag === true || flag === 1 || flag === '1'
}

function pickPreferredPlan(plans: AnyRecord[]): AnyRecord | null {
  const bound = plans.filter((plan) => Number(plan?.id) > 0)
  const featuredYearly = bound.find((plan) => normalizePeriod(plan) === 'year' && normalizePrice(plan?.price) === '$69.99')
  if (featuredYearly) return featuredYearly
  const weeklyIntro = bound.find((plan) => (
    normalizePeriod(plan) === 'week'
    && normalizePrice(plan?.price) === '$12.99'
    && hasIntroDiscount(plan)
  ))
  if (weeklyIntro) return weeklyIntro
  const weeklyLegacy = bound.find((plan) => normalizePeriod(plan) === 'week' && normalizePrice(plan?.price) === '$9.99')
  if (weeklyLegacy) return weeklyLegacy
  const monthly = bound.find((plan) => {
    const price = normalizePrice(plan?.price)
    return normalizePeriod(plan) === 'month' && (price === '$29.99' || price === '$29.90')
  })
  if (monthly) return monthly
  const yearly = bound.find((plan) => normalizePeriod(plan) === 'year' && Boolean(normalizePrice(plan?.price)))
  if (yearly) return yearly
  return bound[0] || null
}

function displayPrice(plan: AnyRecord, period: string): string {
  const price = normalizePrice(plan?.price)
  if (period === 'week' && price === '$12.99' && hasIntroDiscount(plan)) return '$6.99'
  if (period === 'month' && (price === '$29.90' || price === '$29.99')) return '$29.99'
  return price
}

export function buildPcVipPlanPresentation(payload: AnyRecord | null | undefined): PcVipPlanPresentation {
  const rawPlans = payload?.vip_packages
  const plans = Array.isArray(rawPlans) ? rawPlans : []
  const plan = pickPreferredPlan(plans)
  const period = plan ? normalizePeriod(plan) : ''
  const price = plan ? displayPrice(plan, period) : ''

  if (!plan || !price || !period) {
    return {
      available: false,
      planId: null,
      primary: 'See current eligible plan',
      secondary: 'Choose a plan on the next step before checkout.',
      renewal: 'Billing period and amount are shown before you pay.',
      cta: 'Continue to choose a plan',
    }
  }

  if (period === 'week' && price === '$6.99' && hasIntroDiscount(plan)) {
    return {
      available: true,
      planId: Number(plan.id),
      primary: 'First week $6.99',
      secondary: 'Then $12.99/week · Web checkout',
      renewal: 'Auto-renews weekly at $12.99 until canceled.',
      cta: 'Continue · First week $6.99',
    }
  }

  const recurring = `${price}/${period}`
  return {
    available: true,
    planId: Number(plan.id),
    primary: recurring,
    secondary: `Web plan · ${recurring}`,
    renewal: `Auto-renews ${period === 'week' ? 'weekly' : period === 'month' ? 'monthly' : period === 'year' ? 'yearly' : `every ${period}`} until canceled.`,
    cta: `Continue · ${recurring}`,
  }
}
